// STORMS roll in per DAY CYCLE. Each in-game day has a 25% chance of a storm. When one rolls, its TYPE
// (light / medium / heavy -> peak intensity), its DURATION, and its START TIME-OF-DAY are all picked
// deterministically from the seed + day index. CARRY-OVER: a run of consecutive storm-days is ONE storm whose
// duration DOUBLES per extra day (capped) -- so a wet spell that keeps rolling success just keeps going, longer
// each day. intensity is a pure function of (seed, totalDays), so it's reproducible and MP-ready (a server can
// feed the same day clock). Day or night; the cloud field localises WHERE it actually rains (clouds.ts/rain.ts).

function hash01(n: number): number {
  let h = (n >>> 0) ^ 0x9e3779b9;
  h = Math.imul(h ^ (h >>> 16), 0x45d9f3b);
  h = Math.imul(h ^ (h >>> 16), 0x45d9f3b);
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
}
function smooth01(x: number): number { const t = Math.max(0, Math.min(1, x)); return t * t * (3 - 2 * t); }

const STORM_CHANCE = 0.25;   // chance a given in-game day has a storm
const DUR_CAP = 3.0;         // max storm length in days, after carry-over doubling
const LEAD = 0.1;            // days (~2 in-game min) the clouds gather BEFORE the rain starts (the approach)

export class StormSystem {
  intensity = 0;             // RAIN envelope 0..1 (0 = no rain; peak = this storm's heaviness)
  cover = 0;                 // CLOUD/mood envelope 0..1 -- LEADS the rain by LEAD (the approaching gloom)
  front = 0;                 // approach front sweep 0..1 (0 = at the far horizon, 1 = overhead/here)
  dirX = 0; dirY = 1;        // approach direction (unit XZ) the front rolls in FROM
  peak = 0;                  // active storm's heaviness (light 0.4 / medium 0.7 / heavy 1.0)
  private seed: number;
  constructor(seed = 20260628) { this.seed = seed >>> 0; }

  // MP join: adopt the world's shared seed so every client rolls the identical storm days.
  reseed(seed: number): void { this.seed = seed >>> 0; }

  /** Does in-game day `d` roll a storm? (deterministic 25%) */
  private dayStorms(d: number): boolean { return hash01(this.seed + d * 0x9e3779b1) < STORM_CHANCE; }

  /** Params for the storm RUN that starts on day d0 and spans L consecutive storm-days. */
  private runParams(d0: number, L: number): { start: number; peak: number; dur: number } {
    const h = (salt: number): number => hash01((this.seed ^ 0x2545f491) + d0 * 0x9e3779b1 + salt * 40503);
    const startPhase = 0.06 + h(1) * 0.82;                    // time-of-day to begin (0 = dawn .. 1 = end of night)
    const r = h(2);
    const peak = r < 0.5 ? 0.4 : (r < 0.85 ? 0.7 : 1.0);     // light / medium / heavy storm
    const base = 0.25 + h(3) * 0.35;                          // base length in days (0.25..0.60)
    const dur = Math.min(DUR_CAP, base * Math.pow(2, L - 1)); // CARRY-OVER: double the length per extra storm-day
    return { start: d0 + startPhase, peak, dur };
  }

  update(totalDays: number): void {
    const d = Math.floor(totalDays);
    let rain = 0;                                                  // strongest RAIN envelope
    let bestMood = 0, front = 1, dirX = 0, dirY = 1, peak = 0;     // params of the strongest CLOUD/mood run
    // A storm can extend a few days past its start day (carry-over), so scan run-starts in a small back-window.
    for (let k = d; k >= d - Math.ceil(DUR_CAP) - 2; k--) {
      if (!this.dayStorms(k) || this.dayStorms(k - 1)) continue;   // only RUN STARTS (the day before is dry)
      let L = 1; while (this.dayStorms(k + L)) L++;                // run length (counts forward -> bakes in the doubling)
      const p = this.runParams(k, L);
      const prog = (totalDays - p.start) / p.dur;
      // RAIN: 0 before start, then ramp .. hold .. fade
      if (prog >= 0 && prog <= 1) rain = Math.max(rain, p.peak * smooth01(prog / 0.18) * (1.0 - smooth01((prog - 0.72) / 0.28)));
      // CLOUD/MOOD: leads the rain by LEAD (gathers over the approach), holds, fades with the same tail.
      const aStart = p.start - LEAD;
      if (totalDays < aStart || totalDays > p.start + p.dur) continue; // outside the approach+storm window
      const covEnv = totalDays < p.start
        ? smooth01((totalDays - aStart) / LEAD)                    // approach: ramp 0 -> 1 over the 2 min before rain
        : 1.0 - smooth01((prog - 0.72) / 0.28);                   // body: hold 1, then fade with the tail
      const effMood = covEnv * (0.55 + 0.45 * p.peak);            // heavier storms gather darker
      if (effMood > bestMood) {
        bestMood = effMood;
        front = totalDays < p.start ? smooth01((totalDays - aStart) / LEAD) : 1.0; // front sweeps in, then stays overhead
        const ang = hash01((this.seed ^ 0x9e3779b9) + k * 2654435761) * Math.PI * 2; // seeded approach bearing
        dirX = Math.cos(ang); dirY = Math.sin(ang);
        peak = p.peak;
      }
    }
    this.intensity = rain;
    this.cover = bestMood;
    this.front = front;
    this.dirX = dirX; this.dirY = dirY;
    this.peak = peak;
  }
}

// --- JS port of the cloud-density field (matches clouds.ts oxCloud) ----------------------------------
// Lets the renderer ask "how much cloud is over THIS world point right now", for the player's LOCAL rain
// intensity (drives the storm post-dim + thunder gating). Slight float-precision drift from the GLSL version
// is fine -- it only needs to roughly agree on "is it raining where I'm standing".
function ch(x: number, z: number): number { const s = Math.sin(x * 127.1 + z * 311.7) * 43758.5453; return s - Math.floor(s); }
function cn(x: number, z: number): number {
  const ix = Math.floor(x), iz = Math.floor(z); let fx = x - ix, fz = z - iz; fx = fx * fx * (3 - 2 * fx); fz = fz * fz * (3 - 2 * fz);
  const a = ch(ix, iz), b = ch(ix + 1, iz), c = ch(ix, iz + 1), d = ch(ix + 1, iz + 1);
  const ab = a + (b - a) * fx, cd = c + (d - c) * fx; return ab + (cd - ab) * fz;
}
export function cloudDensityAt(wx: number, wz: number, t: number): number {
  let ux = wx * 0.0016 + t * 0.016, uz = wz * 0.0016 + t * 0.010, s = 0, amp = 0.5;
  for (let i = 0; i < 5; i++) { s += amp * cn(ux, uz); ux = ux * 2.02 + 0.05 * t * amp; uz = uz * 2.02 + 0.05 * t * amp; amp *= 0.5; }
  return s;
}
